import React, { useState } from 'react';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import ABNField from './ABNField'
import useRegistrationForm from '../hooks/useRegistrationForm';
import FormField from './FormField';
import AddressFields from './AddressFields';
import WholesalerSelect from './WholesalerSelect';
import PhotoUpload from './PhotoUpload';
import logo from '../assets/td-connect-logo.png';
import tdLogo from '../assets/td-logo.png';
import traxLogo from '../assets/trax-logo.png';
import '../styles/form.css';

function RegisterFormInner() {
  const {
    form,
    wholesalers,
    status,
    abnStatus,
    businessNameOptions,
    handleChange,
    handleFileChange,
    handleSubmit,
    handleAddressAutofill,
    handleAbnLookup,
  } = useRegistrationForm();

  const { executeRecaptcha } = useGoogleReCaptcha();
  const [submitted, setSubmitted] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();

    if (!executeRecaptcha) {
      console.error('reCAPTCHA not yet ready');
      return;
    }

    const captchaToken = await executeRecaptcha('register_submit');
    const ok = await handleSubmit(e, captchaToken);
    if (ok) setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="form-container">
        <div className="form-logo-wrapper">
          <img src={logo} alt="Company Logo" className="form-logo" />
        </div>
        <div className="registration-success" role="status">
          <h2>Registration submitted successfully</h2>
          <p>
            Tobacco Wholesaler TRAX will contact you via email within two
            business days to set up your account.
          </p>
          <p>
            Once your account has been established, you will be able to
            start ordering tobacco products.
          </p>
          <p>Thank you for Registering</p>
        </div>
      </div>
    );
  }

  return (
    <div className="form-container">
      <div className="form-logo-wrapper">
        <img src={logo} alt="Company Logo" className="form-logo" />
      </div>

      <h2>Retailer Registration Form</h2>
      <p className="form-subnote">
        Fields marked with <span className="required-asterisk">*</span> are required.
      </p>

      <form onSubmit={onSubmit}>
        <ABNField
          value={form.abn}
          onChange={handleChange}
          onLookup={handleAbnLookup}
          status={abnStatus}
          businessName={form.businessName}
          businessNameOptions={businessNameOptions}
          onSelectBusinessName={(name) =>
            handleChange({ target: { name: 'businessName', value: name } })
          }
        />

        <FormField
          label="Contact Name"
          name="contactName"
          value={form.contactName}
          onChange={handleChange}
          required
          hint="Full name of the person we should contact"
        />

        <AddressFields
          form={form}
          onChange={handleChange}
          onAddressAutofill={handleAddressAutofill}
        />

        <FormField
          label="Phone Number"
          name="phoneNumber"
          value={form.phoneNumber}
          onChange={handleChange}
          required
          hint="Best contact number, including area code"
        />

        <FormField
          label="Email Address"
          name="emailAddress"
          type="email"
          value={form.emailAddress}
          onChange={handleChange}
          required
          hint="We'll send your confirmation to this address"
        />

        <FormField
          label="Confirm Email Address"
          name="confirmEmailAddress"
          type="email"
          value={form.confirmEmailAddress}
          onChange={handleChange}
          required
          hint="Please re-enter your email address to confirm (paste disabled)"
          blockPaste
        />

        <WholesalerSelect
          state={form.state}
          wholesaler={form.wholesaler}
          wholesalers={wholesalers}
          onChange={handleChange}
          accountNumber={form.wholesalerAccountNumber}
        />

        <FormField
          label="Tobacco Licence Number"
          name="tobaccoLicenceNumber"
          value={form.tobaccoLicenceNumber}
          onChange={handleChange}
          required
          hint="As shown on your tobacco retail licence"
        />

        <FormField
          label="Expiry Date"
          name="expiryDate"
          type="date"
          value={form.expiryDate}
          onChange={handleChange}
          required
          hint="Expiry date of your tobacco licence"
        />

        <PhotoUpload onChange={handleFileChange} />

        <button type="submit" disabled={status.loading} className="submit-btn">
          {status.loading ? 'Submitting...' : 'Submit Registration'}
        </button>

        {status.message && (
          <p className={status.error ? 'msg-error' : 'msg-success'}>
            {status.message}
          </p>
        )}
      </form>

      <div className="partner-links">
        <a
          href="https://the-distributors.com.au/"
          target="_blank"
          rel="noreferrer"
          className="partner-link"
        >
          <img src={tdLogo} alt="The Distributors" className="partner-logo" />
          <span className="partner-url">the-distributors.com.au</span>
        </a>

        <a
          href="https://www.traxdistribution.com.au/"
          target="_blank"
          rel="noreferrer"
          className="partner-link"
        >
          <img src={traxLogo} alt="TRAX Distribution" className="partner-logo" />
          <span className="partner-url">traxdistribution.com.au</span>
        </a>
      </div>
      
      <p className="recaptcha-disclosure">
        This site is protected by reCAPTCHA and the Google{' '}
        <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">Privacy Policy</a> and{' '}
        <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer">Terms of Service</a> apply.
      </p>
    </div>
  );
}

export default function RegisterForm() {
  return (
    <GoogleReCaptchaProvider reCaptchaKey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}>
      <RegisterFormInner />
    </GoogleReCaptchaProvider>
  );
}
